import React, { useState } from 'react'
import smartDate from '@qrohlf/smart-date'
import { CommentBox } from '../CommentBox'
import styles from './Comment.scss'
import { Badge } from 'common/Badge'
import { ProfileImg } from 'common/ProfileImg'
import { commonVerbs, useFormatMessage } from 'lib/localization'
import { OrderedMap } from 'immutable'
import { defineMessages, injectIntl } from 'react-intl'

const messages = defineMessages({
  placeholder: {
    id: 'ReplyBox.placeholder',
    defaultMessage: 'Reply to this comment...',
    description: 'Placeholder text for replying to a comment'
  },
  reply: {
    id: 'ReplyBox.reply',
    defaultMessage: 'Reply',
    description: 'A button to open the reply box'
  },
  hideReply: {
    id: 'ReplyBox.hideReply',
    defaultMessage: 'Hide Reply',
    description: 'A button to hide the reply box'
  },
  confirmDelete: {
    id: 'Comment.confirmDelete',
    defaultMessage: 'Are you sure you want to delete this comment?',
    description: 'A modal asking to confirm deletion of a comment'
  }
})

export const _Comment = ({ comment, isAdmin, threadable, replies, submitReply, deleteComment, canDelete, setExpand, showTime, currentUser }) => {
  const fm = useFormatMessage()
  const [showReplyBox, toggleShowReplyBox] = useState(false)

  const confirmAndDelete = (event, comment) => {
    event.preventDefault()
    event.stopPropagation()
    if (window.confirm(fm(messages.confirmDelete))) {
      deleteComment(comment.parent_type_id, comment.parent_id, comment.id)
    }
  }

  const submitReplyAndHideBox = replyText => {
    submitReply('comment', comment.id, replyText)
    toggleShowReplyBox(!showReplyBox)
    // the usage of this function in CommentBox.js expects it to return a promise
    return new Promise((resolve, reject) => { return resolve() })
  }

  const reply = event => {
    event.preventDefault()
    event.stopPropagation()
    setExpand()
    toggleShowReplyBox(!showReplyBox)
  }

  const commentReplies = replies && replies.getIn([comment.id, 'comments'], OrderedMap())
  const childProps = { threadable, showTime, canDelete, isAdmin, confirmAndDelete, reply, showReplyBox, fm }

  return (
    <div className={styles.Comment}>
      <FormattedComment
        rawComment={comment}
        {...childProps}
      />
      <div className={styles.Reply}>
        {commentReplies && commentReplies.size > 0 && commentReplies.map((reply, index) => <FormattedComment key={index} rawComment={reply} {...childProps} />).valueSeq()}
        {showReplyBox && <CommentBox loggedIn={!!currentUser.id} onSubmit={submitReplyAndHideBox} placeholder={fm(messages.placeholder)} />}
      </div>
    </div>
  )
}

export const Comment = injectIntl(_Comment)

const FormattedComment = ({ rawComment, threadable, showTime, canDelete, isAdmin, confirmAndDelete, reply, showReplyBox, fm }) => {
  const isAdminReply = threadable && rawComment.user.account_level === 11
  const canActuallyDelete = isAdminReply
    ? (canDelete && isAdmin)
    : canDelete
  return (
  <>
    <div className={styles.userIcon}>
      <ProfileImg user={rawComment.user} userId={rawComment.user.id} highlightedPhotoId={rawComment.user.highlighted_photo_id} flair={isAdminReply ? 'logo-rwgps' : ''} />
    </div>

    <div className={styles.content}>
      <div className={styles.header}>
        <a href={`/users/${rawComment.user.id}`}>{rawComment.user.name}</a>
        {threadable && isAdminReply && <Badge type='commentAdmin'>Admin</Badge>}
        <span className={styles.dot}>∙</span>
        <span className={styles.date} title={showTime && smartDate(rawComment.created_at, { includeTime: true, includeYear: true })}>
          {smartDate(rawComment.created_at, { includeTime: !!showTime, includeYear: !showTime })}</span>
        {canActuallyDelete && <span>
          <span className={styles.dot}>∙</span>
          <a href='#' className={styles.delete} onClick={e => confirmAndDelete(e, rawComment)}>{fm(commonVerbs.delete)}</a>
        </span>}
        {/* only allow admins to reply, only allow replies to top-level comments */}
        {threadable && isAdmin && rawComment.parent_type_id !== 14 &&
          <span>
            <span className={styles.dot}>∙</span>
            <a href='#' className={styles.reply} onClick={reply}>{showReplyBox ? fm(messages.hideReply) : fm(messages.reply)}</a>
          </span>
        }
      </div>
      <div className={styles.body}>
        {rawComment.comment}
      </div>
    </div>
  </>
  )
}
